import React, { useCallback, useEffect, useState } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import Text from '@amzn/meridian/text';
import Alert from '@amzn/meridian/alert';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import Input from '@amzn/meridian/input';
import Modal from '@amzn/meridian/modal';
import {
  getShiftPlanSettings,
  setShiftPlanSettings,
  getCoralServiceDataFromParent,
  getUserLoginFromParent
} from '../../handler/parentDataHandler';
import { getStationCode } from '../../utils/networkUtil';
import { TEXTS } from '../../utils/constants';
import { normalizeTime } from '../../utils/helpers';
import { publishFlowPlannerCountMetric, FLOW_PLANNER_METRICS as M } from '../../utils/metricUtil';

const EMPTY_TIME = '--:--';
const DEFAULT_PERIODS = ['P1', 'P2', 'P3'];

const TimeDistributions = () => {
  const [intervals, setIntervals] = useState(
    DEFAULT_PERIODS.map((name) => ({ windowName: name, startTime: '', endTime: '' }))
  );
  const [savedIntervals, setSavedIntervals] = useState(null);
  const [lastUpdatedBy, setLastUpdatedBy] = useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('success');
  const [isOpen, setIsOpen] = useState(false);
  const [bounds, setBounds] = useState(null);

  useEffect(() => {
    const fetchOpsClock = async () => {
      try {
        const args = {
          processName: 'lmOpsClockService',
          endPoint: 'getOpsClock',
          request: JSON.stringify({ nodeId: getStationCode(), dispatchDate: new Date().toISOString().split('T')[0] })
        };
        const result = await getCoralServiceDataFromParent(args);
        if (result.status === 200 && result.data?.opsClock?.clockEvents) {
          const events = result.data.opsClock.clockEvents;
          const tz = result.data.nodeTimeZone;
          if (!tz) {
            console.error('OpsClock response missing nodeTimeZone');
            return;
          }
          const cycle1Induct = events.find((e) => e.process === 'INDUCT' && e.cycleName === 'CYCLE_1');
          const cycle1Dispatch = events.find((e) => e.process === 'DISPATCH' && e.cycleName === 'CYCLE_1');
          if (cycle1Induct?.timeIntervals?.[0]?.startTime && cycle1Dispatch?.timeIntervals?.[0]?.endTime) {
            const toHHMM = (epoch) => {
              const d = new Date(epoch * 1000);
              const parts = d
                .toLocaleString('en-US', { timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false })
                .split(':');
              const h = parts[0] === '24' ? '00' : parts[0].padStart(2, '0');
              return `${h}:${parts[1]}`;
            };
            const toMins = (epoch) => {
              const d = new Date(epoch * 1000);
              const h = parseInt(d.toLocaleString('en-US', { timeZone: tz, hour: 'numeric', hour12: false }), 10);
              const m = parseInt(d.toLocaleString('en-US', { timeZone: tz, minute: 'numeric' }), 10);
              return (h === 24 ? 0 : h) * 60 + m;
            };
            setBounds({
              min: toHHMM(cycle1Induct.timeIntervals[0].startTime),
              max: toHHMM(cycle1Dispatch.timeIntervals[0].endTime),
              minMins: toMins(cycle1Induct.timeIntervals[0].startTime),
              maxMins: toMins(cycle1Dispatch.timeIntervals[0].endTime)
            });
          }
        }
      } catch (e) {
        console.error('Failed to fetch OpsClock data', e);
      }
    };
    fetchOpsClock();
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const request = {
        operationType: 'get',
        queryParams: 'stationCode=' + getStationCode() + '&configType=TIME_DISTRIBUTIONS'
      };
      const response = await getShiftPlanSettings(request);
      if (response.status === 200) {
        const s3Response = await fetch(response.data.url);
        if (s3Response.status === 200) {
          const data = await s3Response.json();
          if (data.intervals && data.intervals.length > 0) {
            setSavedIntervals(data.intervals);
            setIntervals(data.intervals.map((i) => ({ windowName: i.windowName, startTime: '', endTime: '' })));
          }
          if (data.lastUpdatedBy) setLastUpdatedBy(data.lastUpdatedBy);
          if (data.lastUpdatedAt) setLastUpdatedAt(data.lastUpdatedAt);
        }
      }
    } catch (e) {
      console.error('Failed to load time distributions', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) loadData();
  }, [isOpen, loadData]);

  const updateInterval = (index, field, value) => {
    setIntervals((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const getNextPeriodName = () => {
    const existing = new Set(intervals.map((i) => i.windowName));
    let n = 1;
    while (existing.has('P' + n)) n++;
    return 'P' + n;
  };

  const addPeriod = () => {
    setIntervals((prev) => [...prev, { windowName: getNextPeriodName(), startTime: '', endTime: '' }]);
  };

  const removePeriod = (index) => {
    setIntervals((prev) => prev.filter((_, i) => i !== index));
  };

  const getSavedTime = (windowName, field) => {
    if (!savedIntervals) return EMPTY_TIME;
    const saved = savedIntervals.find((i) => i.windowName === windowName);
    if (!saved || !saved[field]) return EMPTY_TIME;
    return saved[field];
  };

  const hasChanges = () => {
    if (!savedIntervals) {
      return (
        intervals.length !== DEFAULT_PERIODS.length || intervals.some((i) => i.startTime !== '' || i.endTime !== '')
      );
    }
    if (intervals.length !== savedIntervals.length) return true;
    return intervals.some((i) => i.startTime !== '' || i.endTime !== '');
  };

  const isTimeInBounds = (time) => {
    if (!time || !bounds) return true;
    const normalized = normalizeTime(time);
    if (!normalized) return true;
    const [h, m] = normalized.split(':').map(Number);
    const mins = h * 60 + m;
    if (bounds.minMins <= bounds.maxMins) {
      return mins >= bounds.minMins && mins <= bounds.maxMins;
    }
    return mins >= bounds.minMins || mins <= bounds.maxMins;
  };

  const isValid = () => {
    return intervals.every((i) => {
      if (i.startTime && normalizeTime(i.startTime) === null) return false;
      if (i.endTime && normalizeTime(i.endTime) === null) return false;
      if (i.startTime && !isTimeInBounds(i.startTime)) return false;
      if (i.endTime && !isTimeInBounds(i.endTime)) return false;
      return true;
    });
  };

  const handleSave = async () => {
    if (!isValid()) {
      const hasFormatError = intervals.some(
        (i) => (i.startTime && normalizeTime(i.startTime) === null) || (i.endTime && normalizeTime(i.endTime) === null)
      );
      setAlertType('error');
      setAlertMessage(
        hasFormatError
          ? 'Times must be in HH:MM format.'
          : `Times must be within the valid range: ${bounds.min} - ${bounds.max}`
      );
      return;
    }
    try {
      const merged = intervals
        .map((i) => {
          const s = savedIntervals ? savedIntervals.find((x) => x.windowName === i.windowName) : null;
          return {
            windowName: i.windowName,
            startTime: normalizeTime(i.startTime) || (s ? s.startTime : ''),
            endTime: normalizeTime(i.endTime) || (s ? s.endTime : '')
          };
        })
        .filter((i) => i.startTime || i.endTime);

      const request = {
        operationType: 'upload',
        queryParams: 'stationCode=' + getStationCode() + '&configType=TIME_DISTRIBUTIONS'
      };
      const response = await setShiftPlanSettings(request);
      if (response.status === 200) {
        const userLogin = await getUserLoginFromParent()
          .then((r) => r.name || 'unknown')
          .catch(() => 'unknown');
        const now = Math.round(Date.now() / 1000);
        const body = JSON.stringify({
          version: '1.0',
          intervals: merged,
          lastUpdatedBy: userLogin,
          lastUpdatedAt: now
        });
        const putResponse = await fetch(response.data.url, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body
        });
        if (!putResponse.ok) {
          throw new Error('S3 upload failed with status ' + putResponse.status);
        }
        publishFlowPlannerCountMetric(M.OVERRIDE_TIME_DISTRIBUTIONS);
        setSavedIntervals(merged);
        setIntervals(merged.map((i) => ({ windowName: i.windowName, startTime: '', endTime: '' })));
        setLastUpdatedBy(userLogin);
        setLastUpdatedAt(now);
        setAlertType('success');
        setAlertMessage('Time Distributions saved successfully! Please Re-run the plan.');
      }
    } catch (e) {
      setAlertType('error');
      setAlertMessage('Failed to save. Error: ' + e);
    }
  };

  const handleReset = () => {
    if (savedIntervals) {
      setIntervals(savedIntervals.map((i) => ({ windowName: i.windowName, startTime: '', endTime: '' })));
    } else {
      setIntervals(DEFAULT_PERIODS.map((name) => ({ windowName: name, startTime: '', endTime: '' })));
    }
    setAlertMessage('');
  };

  if (!isOpen) {
    return (
      <Button type="secondary" size="small" onClick={() => setIsOpen(true)}>
        Time Distributions
      </Button>
    );
  }

  return (
    <>
      <Button type="secondary" size="small" onClick={() => setIsOpen(true)}>
        Time Distributions
      </Button>
      <Modal
        title="Time Distributions"
        open={isOpen}
        onClose={() => {
          setIsOpen(false);
          setAlertMessage('');
        }}
        closeLabel="Close"
        width="800px">
        <Column spacing="300">
          <Row alignmentHorizontal="justify" alignmentVertical="center" width="100%">
            <Row spacing="200">
              <Button type="secondary" size="small" onClick={addPeriod}>
                + Add Period
              </Button>
              <Button type="secondary" size="small" onClick={handleReset} disabled={!hasChanges()}>
                Reset
              </Button>
              <Button type="primary" size="small" onClick={handleSave} disabled={!hasChanges() || !isValid()}>
                Save
              </Button>
            </Row>
            <Column alignmentHorizontal="end" spacing="100">
              {bounds && (
                <Text type="b200" color="secondary">
                  Valid range: {bounds.min} - {bounds.max}
                </Text>
              )}
              {lastUpdatedBy && (
                <Text type="b200" color="secondary">
                  Last updated by {lastUpdatedBy}
                  {lastUpdatedAt ? ' on ' + new Date(lastUpdatedAt * 1000).toLocaleString() : ''}
                </Text>
              )}
            </Column>
          </Row>
          {loading ? (
            <Text>Loading...</Text>
          ) : (
            <Table headerRows={1} showDividers>
              <TableRow>
                <TableCell>Period</TableCell>
                <TableCell>Current Start</TableCell>
                <TableCell>Current End</TableCell>
                <TableCell>Override Start</TableCell>
                <TableCell>Override End</TableCell>
                <TableCell width={40} />
              </TableRow>
              {intervals.map((interval, index) => (
                <TableRow key={interval.windowName + index}>
                  <TableCell>
                    <Text type="b300">{interval.windowName}</Text>
                  </TableCell>
                  <TableCell>
                    <Text type="b300">{getSavedTime(interval.windowName, 'startTime')}</Text>
                  </TableCell>
                  <TableCell>
                    <Text type="b300">{getSavedTime(interval.windowName, 'endTime')}</Text>
                  </TableCell>
                  <TableCell>
                    <Input
                      value={interval.startTime}
                      onChange={(v) => updateInterval(index, 'startTime', v)}
                      size="small"
                      placeholder="HH:MM"
                      error={
                        interval.startTime !== '' &&
                        (normalizeTime(interval.startTime) === null || !isTimeInBounds(interval.startTime))
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={interval.endTime}
                      onChange={(v) => updateInterval(index, 'endTime', v)}
                      size="small"
                      placeholder="HH:MM"
                      error={
                        interval.endTime !== '' &&
                        (normalizeTime(interval.endTime) === null || !isTimeInBounds(interval.endTime))
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Button type="link" size="small" onClick={() => removePeriod(index)}>
                      <span style={{ fontSize: '18px' }}>x</span>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </Table>
          )}
          {alertMessage && (
            <Alert type={alertType} size="medium">
              {alertMessage}
            </Alert>
          )}
        </Column>
      </Modal>
    </>
  );
};

export default TimeDistributions;
